import { store } from 'store';
import { fetchUpdateCompanyPlatforms } from 'store/slices/companyPlatforms';
import { ChatUserModel } from 'types/chat-users/chat-user';
import type { PriorityCompany, PriorityConnection } from 'types/company-platforms/priority';
import {
  PriorityDocInfo,
  PriorityDocType,
  PriorityFormat,
  PriorityPlatformsSettings,
} from 'types/company-platforms/priority';
import { fetcher } from 'utils/axios';
import type { Procedure } from 'views/priority/priority-settings-page/companies/ProceduresList';

// Priority Comapies
export const fetchCreatePriorityCompany = async (
  company: Omit<PriorityCompany, 'id' | 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy' | 'companyId'>,
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/priority/companies',
        {
          data: company,
        },
      ],
      'newapi',
    );

    if (data?.result) {
      // Update the company platforms in the store
      const storeState = store.getState();
      const updatedCompanyPlatforms = { ...storeState.companyPlatforms.priority, companies: data.data };
      await fetchUpdateCompanyPlatforms({ priority: updatedCompanyPlatforms });
    }

    return data;
  } catch (error) {
    console.warn('Error in fetchCreatePriorityCompany:', error);
    return null;
  }
};

export const fetchGetPriorityCompanies = async () => {
  try {
    const data = await fetcher('get', '/api/priority/companies', 'newapi');
    if (data.error) {
      throw new Error(data.error);
    }
    if (data?.data) return data.data as Array<PriorityCompany>;
  } catch (error) {
    console.warn('Error in fetchGetPriorityCompanies:', error);
    return [] as Array<PriorityCompany>;
  }
};

export const fetchUpdatePriorityCompany = async (company: PriorityCompany) => {
  try {
    const data = await fetcher(
      'put',
      [
        `/api/priority/companies/${company.id}`,
        {
          data: company,
        },
      ],
      'newapi',
    );

    if (data?.result) {
      // Update the company platforms in the store
      const storeState = store.getState();
      const updatedCompanyPlatforms = { ...storeState.companyPlatforms.priority, companies: data.data };
      await fetchUpdateCompanyPlatforms({ priority: updatedCompanyPlatforms });
    }

    return data;
  } catch (error) {
    console.warn('Error in fetchUpdatePriorityCompany:', error);
    return null;
  }
};

export const fetchDeletePriorityCompany = async (companyId: string) => {
  try {
    const data = await fetcher('delete', `/api/priority/companies/${companyId}`, 'newapi');

    if (data?.result) {
      // Update the company platforms in the store
      const storeState = store.getState();
      const updatedCompanyPlatforms = { ...storeState.companyPlatforms.priority, companies: data.data };
      await fetchUpdateCompanyPlatforms({ priority: updatedCompanyPlatforms });
    }

    return data;
  } catch (error) {
    console.warn('Error in fetchDeletePriorityCompany:', error);
    return null;
  }
};

//* procedures
export const fetchGetPriorityDocTypes = async () => {
  try {
    const data = await fetcher('get', '/api/priority/getDocTypes', 'api');

    if (data) return data as Array<PriorityDocType>;
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityDocTypes:', error);
    return null;
  }
};
export const fetchCreateEditProcedure = async (
  procedure: Omit<PriorityDocType, 'formats' | 'companyId' | 'id'>,
) => {
  try {
    const data = await fetcher(
      'put',
      [
        `/priorityProceedures/${procedure.procedureName}`,
        {
          data: procedure,
        },
      ],
      'newapi',
    );

    // if (data) return data as PriorityDocType;
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in fetchCreateEditProcedure:', error);
    return null;
  }
};
export const fetchDeleteProcedure = async (procedureName: string) => {
  try {
    const data = await fetcher('delete', `/priorityProceedures/${procedureName}`, 'api');

    // if (data) return data as PriorityDocType;
    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in fetchDeleteProcedure:', error);
    return null;
  }
};
export const fetchDuplicateProcedure = async (procedureName: string) => {
  try {
    const data = await fetcher('put', `/priorityProceedures/duplicate/${procedureName}`, 'api');

    if (data) return data;
    else return null;
  } catch (error) {
    console.warn('Error in fetchDuplicateProcedure:', error);
    return null;
  }
};

export type SaveCompanyProcedureType = {
  CompanyId: string;
  DateUpdated: string;
  FormatObject: string | null;
  Id: number;
  ProcedureName: string;
};
export const saveAllCompanyFormats = async () => {
  try {
    const data = await fetcher('get', '/api/priority/saveAllCompanyFormats', 'priority');

    if (data)
      return data as {
        result: boolean;
        companyFormats: Array<SaveCompanyProcedureType>;
      };
    else return null;
  } catch (error) {
    console.warn('Error in saveAllCompanyFormats:', error);
    return null;
  }
};

//* docs
export type FetchGetPriorityDocsListParams = {
  priorityCompanyId?: string;
  docType: string;
  docSearchIndex?: string;
  from?: string;
  to?: string;
  name?: string;
  docId?: string;
  filter?: string;
  top?: number;
  skip: number;
};
export const fetchGetPriorityDocsList = async (params: FetchGetPriorityDocsListParams) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityDocsList',
        {
          data: params,
        },
      ],
      'newapi',
    );

    if (data) {
      return data as {
        error: string;
        result: boolean;
        docs: string;
        url: string;
        procedureObject: {
          formats: Array<PriorityFormat>;
          sorting: string;
          title: string;
          docTitle: string;
          procedureName: string;
          docIndetificator: string;
          idIdentifier: string;
          sdkLinkType: string;
        };
        formats?: {
          CompanyId: string;
          DateUpdated: string;
          FormatObject: string | null;
          Id: number;
        } | null;
      };
    } else {
      // אם השרת החזיר תגובה ריקה
      return {
        error: 'השרת החזיר תגובה ריקה',
        result: false,
        docs: '',
        url: '',
        procedureObject: null,
      } as any;
    }
  } catch (error: any) {
    console.warn('Error in fetchGetPriorityDocsList:', error);

    // החזרת אובייקט שגיאה במקום null
    return {
      error: error?.message || error?.response?.data?.message || 'שגיאה בתקשורת עם השרת',
      result: false,
      docs: '',
      url: '',
      procedureObject: null,
      isNetworkError: true,
    } as any;
  }
};
export const fetchGetPriorityDocFormatsFromDoc = async (
  docId: string,
  docType: string,
  procedureName: string,
  signedDoc?: boolean,
) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityDoc',
        {
          data: { docId, docType, procedureName, signedDoc },
        },
      ],
      'priority',
    );

    if (data)
      return data as {
        formats: Array<PriorityFormat> | null;
        errorMessage: string | null;
      };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityDocFormatsFromDoc:', error);
    return null;
  }
};

export const fetchGetPriorityDocumentInfo = async (
  priorityCompanyId: string,
  docId: string,
  docType: string,
  procedureId: string,
  format: string | number,
  // isAutomail?: boolean,
) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityDoc',
        {
          data: {
            priorityCompanyId,
            docId,
            docType,
            procedureId,
            format,
            // isAutomail,
          },
        },
      ],
      'newapi',
    );

    if (data) return data as PriorityDocInfo;
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityDocumentInfo:', error);
    return null;
  }
};

//* settings
export const submitPriorityParams = async (params: Partial<PriorityPlatformsSettings>) => {
  const storeState = store.getState();

  const newParams = { ...storeState.companyPlatforms.priority, ...params };
  return await fetchUpdateCompanyPlatforms({ priority: newParams });
};

//* contacts & customers
export const fetchGetPriorityCustomers = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    
    // אם יש searchTerm, נשתמש ב-API החדש עם OData
    if (params.searchTerm && params.priorityCompanyId) {
      
      // בניית פילטר OData לחיפוש
      const searchTerm = params.searchTerm;
      const filter = `PHONE EQ '${searchTerm}*' OR CUSTNAME EQ '${searchTerm}*' OR CUSTDES EQ '${searchTerm}*'`;
      
      const queryParams = new URLSearchParams({
        '$TOP': params.top.toString(),
        '$SKIP': params.skip.toString(),
        '$FILTER': filter
      });
      
      const url = `/api/priority/query/${params.priorityCompanyId}/CUSTOMERS?${queryParams.toString()}`;
    
      const data = await fetcher('get', url, 'newapi');
    
      if (data && data.data) {
        // המרת התגובה לפורמט הצפוי - הנתונים נמצאים ב-data.data
    
        return {
          contacts: JSON.stringify({ value: [] }), // ריק כי אנחנו מחפשים לקוחות
          customers: JSON.stringify(data.data) // מעביר את data.data שמכיל את value
        };
      }
      return null;
    }
    
    // שימוש ב-API הקיים אם אין חיפוש
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityContacts',
        {
          data: params,
        },
      ],
      'newapi',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityCustomers:', error);
    return null;
  }
};

//* contacts (PHONEBOOK)
export const fetchGetPriorityContacts = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    
    // אם יש searchTerm, נשתמש ב-API החדש עם OData
    if (params.searchTerm && params.priorityCompanyId) {
      
      // בניית פילטר OData לחיפוש ב-PHONEBOOK
      const searchTerm = params.searchTerm;
      const filter = `PHONENUM EQ '${searchTerm}*' OR NAME EQ '${searchTerm}*'`;
      
      const queryParams = new URLSearchParams({
        '$TOP': params.top.toString(),
        '$SKIP': params.skip.toString(),
        '$FILTER': filter
      });
      
      const url = `/api/priority/query/${params.priorityCompanyId}/PHONEBOOK?${queryParams.toString()}`;
      
      const data = await fetcher('get', url, 'newapi');
      
      if (data && data.data) {
        // המרת התגובה לפורמט הצפוי - הנתונים נמצאים ב-data.data
      
        return {
          contacts: JSON.stringify(data.data), // מעביר את data.data שמכיל את value
          customers: JSON.stringify({ value: [] }) // ריק כי אנחנו מחפשים אנשי קשר
        };
      }
      return null;
    }
    
    // שימוש ב-API הקיים אם אין חיפוש
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityContacts',
        {
          data: params,
        },
      ],
      'newapi',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityContacts:', error);
    return null;
  }
};
export const fetchFindPriorityContactByName = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/findPriorityContactByName',
        {
          data: params,
        },
      ],
      'priority',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchFindPriorityContactByName:', error);
    return null;
  }
};

export const fetchFindPriorityCustomerByName = async (params: {
  name: string;
  top: number;
  skip: number;
  priorityCompanyId?: string;
  searchTerm?: string;
}) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/findPriorityCustomerByName',
        {
          data: params,
        },
      ],
      'priority',
    );

    if (data) return data as { contacts: string; customers: string };
    else return null;
  } catch (error) {
    console.warn('Error in fetchFindPriorityCustomerByName:', error);
    return null;
  }
};

export const fetchGetPriorityChatUserObject = async (phoneNumberId: string, userName?: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/getPriorityChatUserObject',
        {
          data: { phoneNumberId, userName },
        },
      ],
      'priority',
    );

    if (data) return data as ChatUserModel;
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityChatUserObject:', error);
    return null;
  }
};
export const fetchGetPriorityCustomerPhonebook = async (params: { name: string }) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/priority/getPriorityCustomerPhonebook',
        {
          data: params,
        },
      ],
      'priority',
    );

    if (data) return data as { phonebook: string; result: boolean };
    else return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityCustomerPhonebook:', error);
    return null;
  }
};

// Priority Connections
// עדכון חתימות הפונקציות ל-camelCase
export const fetchCreatePriorityConnection = async (
  connection: Omit<
    PriorityConnection,
    'connectionId' | 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy' | 'companyId'
  >,
) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/priority/connections',
        {
          data: connection,
        },
      ],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchCreatePriorityConnection:', error);
    return null;
  }
};

export const fetchGetPriorityConnections = async () => {
  try {
    const data = await fetcher('get', '/api/priority/connections', 'newapi');
    if (data.error) {
      throw new Error(data.error);
    }
    if (data?.data) return data.data as Array<PriorityConnection>;
  } catch (error) {
    console.warn('Error in fetchGetPriorityConnections:', error);
    return [] as Array<PriorityConnection>;
  }
};

export const fetchUpdatePriorityConnection = async (connection: PriorityConnection) => {
  try {
    const data = await fetcher(
      'put',
      [
        `/api/priority/connections/${connection.connectionId}`,
        {
          data: connection,
        },
      ],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchUpdatePriorityConnection:', error);
    return null;
  }
};

export const fetchDeletePriorityConnection = async (connectionId: string) => {
  try {
    const data = await fetcher('delete', `/api/priority/connections/${connectionId}`, 'newapi');
    return data;
  } catch (error) {
    console.warn('Error in fetchDeletePriorityConnection:', error);
    return null;
  }
};

// Get procedures for a specific Priority company
export const fetchGetCompanyProcedures = async (prCompanyId: string) => {
  try {
    const data = await fetcher('get', `/api/priority/companies/${prCompanyId}/procedures`, 'newapi');
    if (data?.data) return data.data;
    return [];
  } catch (error) {
    console.warn('Error in fetchGetCompanyProcedures:', error);
    return [];
  }
};

// Create a new procedure for a company
export const fetchCreateCompanyProcedure = async (
  prCompanyId: string,
  procedure: Omit<
    Procedure,
    'procId' | 'createdOn' | 'createdBy' | 'updatedOn' | 'updatedBy' | 'companyId' | 'prCompanyId'
  >,
) => {
  try {
    const data = await fetcher(
      'post',
      [`/api/priority/companies/${prCompanyId}/procedures`, { data: procedure }],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchCreateCompanyProcedure:', error);
    return null;
  }
};

// Update a procedure for a company
export const fetchUpdateCompanyProcedure = async (prCompanyId: string, procedure: Procedure) => {
  try {
    const data = await fetcher(
      'put',
      [`/api/priority/companies/${prCompanyId}/procedures/${procedure.procId}`, { data: procedure }],
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchUpdateCompanyProcedure:', error);
    return null;
  }
};

// Delete a procedure for a company
export const fetchDeleteCompanyProcedure = async (prCompanyId: string, procId: string) => {
  try {
    const data = await fetcher(
      'delete',
      `/api/priority/companies/${prCompanyId}/procedures/${procId}`,
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchDeleteCompanyProcedure:', error);
    return null;
  }
};

// Get all procedures from all companies with company information
export const fetchGetAllCompanyProcedures = async () => {
  try {
    const data = await fetcher('get', '/api/priority/companies/procedures', 'newapi');
    if (data?.data) return data.data;
    return [];
  } catch (error) {
    console.warn('Error in fetchGetAllCompanyProcedures:', error);
    return [];
  }
};

// Sync procedure formats for a company procedure
export const fetchSyncProcedureFormats = async (prCompanyId: string, procedureId: string) => {
  try {
    const data = await fetcher(
      'post',
      `/api/priority/companies/${prCompanyId}/procedures/${procedureId}/sync-formats`,
      'newapi',
    );
    return data;
  } catch (error) {
    console.warn('Error in fetchSyncProcedureFormats:', error);
    return null;
  }
};

// New Priority Config API calls
export interface PriorityConfigData {
  priorityFullApiUrl: string | null;
  priorityApiUrl: string | null;
  priorityUiUrl: string | null;
  priorityTabula: string | null;
  priorityLinkType: string | null;
  priorityToken: string | null;
  priorityConnectionVersion: string | null;
}

export const fetchGetPriorityConfig = async (): Promise<PriorityConfigData | null> => {
  try {
    const data = await fetcher('get', '/api/priority/getPriorityConfig', 'newapi');
    if (data?.data) {
      return data.data as PriorityConfigData;
    }
    return null;
  } catch (error) {
    console.warn('Error in fetchGetPriorityConfig:', error);
    return null;
  }
};

export const fetchUpdatePriorityConfig = async (
  config: Partial<PriorityConfigData>,
): Promise<{ success: boolean; message: string }> => {
  try {
    const data = await fetcher(
      'put',
      [
        '/api/priority/updatePriorityConfig',
        {
          data: config,
        },
      ],
      'newapi',
    );

    if (data?.error) {
      return { success: false, message: data.error };
    } else {
      return { success: true, message: data?.data || 'הגדרות פריוריטי עודכנו בהצלחה' };
    }
  } catch (error) {
    console.warn('Error in fetchUpdatePriorityConfig:', error);
    return { success: false, message: 'שגיאה בתקשורת עם השרת' };
  }
};
